import { Injectable, Inject } from '@angular/core';
import { ApiService } from 'src/app/core/services/api.service';

import {
  ClientDataRequestEvents,
  ContractModificationRequestEvents,
  RequestCommentEvents,
  RequestDocumentsEvents,
  RequestEvents,
} from './events';
import { DEFAULT_PAGE_SIZE } from '../../constants/commonConstants';
import { ENV_CONFIG } from 'src/app/core/injection.token';

type DynamicResponse<T> = app.models.DynamicResponse<T>;
type DynamicPaged<T> = app.models.DynamicResponse<app.models.PagedResult<T>>;

@Injectable()
export class RequestAPIService {
  public constructor(
    @Inject(ApiService) private _apiService: ApiService,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvData
  ) {}

  private readonly controller = 'request';

  public saveRequest(requestBody: any) {
    const request = 'saveRequest';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const successEvents = [RequestEvents.RequestSaved];

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, requestBody, successEvents);
  }

  public saveDelegate(requestBody: app.models.Delegate) {
    const request = 'saveDelegate';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, requestBody, [RequestEvents.DelegateSaved]);
  }

  public removeDelegate(delegateId: string) {
    const request = 'removeDelegate';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const body = { delegateId: delegateId };

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, body, [RequestEvents.DelegateRemoved]);
  }

  public getDelegates() {
    const request = 'getDelegates';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.Delegate[]>(url);
  }

  public getDocumentRequestHeader(contractId: string, assetId: string = null) {
    const request = 'getDocumentRequestHeader';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      contractId: contractId,
      assetId: assetId,
    });

    return this._apiService.get<app.models.DocumentRequestHeader>(url);
  }

  public getRequests(pageIndex: number, pageFilter: app.models.GetContractModificationRequestsModelPageFilter) {
    const request = 'getRequests';
    let data = {
      ...pageFilter,
      page: pageIndex,
      pageSize: DEFAULT_PAGE_SIZE,
    };

    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.post<DynamicPaged<app.models.Requests>>(url, data);
  }

  public getRequest(requestId: string) {
    const request = 'getRequest';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    const params = { requestId: requestId };

    return this._apiService.get<app.models.DynamicResponse<app.models.Requests>>(url, params);
  }

  public getContractModificationRequestHeader(contractId: string) {
    const request = 'getContractModificationRequestHeader';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      contractId: contractId,
    });

    return this._apiService.get<app.models.ContractModificationRequestHeader>(url);
  }

  public saveContractModificationRequest(requestBody: any) {
    const request = 'saveContractModificationRequest';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, requestBody, [
      ContractModificationRequestEvents.ContractModificationRequestSaved,
    ]);
  }

  public getPagedRequestsForAdministration(
    pageIndex: number,
    filters: app.models.GetRequestsForAdministrationModelPageFilter
  ) {
    const pageFilter = {
      page: pageIndex,
      pageSize: DEFAULT_PAGE_SIZE,
      startDate: filters?.startDate,
      endDate: filters?.endDate,
      clientIds: filters?.partners?.map(x => x.partnerId),
      requestTypeId: filters?.requestTypeId,
      adminUser: filters?.adminUser,
    };

    const request = 'getPagedRequestsForAdministration';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.post<DynamicPaged<app.models.RequestListModel>>(url, pageFilter);
  }

  public getContractModificationRequests(
    pageIndex: number,
    pageFilter: app.models.GetContractModificationRequestsModelPageFilter
  ) {
    const request = 'getContractModificationRequests';
    let data = {
      ...pageFilter,
      page: pageIndex,
      pageSize: DEFAULT_PAGE_SIZE,
    };

    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.post<DynamicPaged<app.models.ContractModificationRequest>>(url, data);
  }

  public getContractModificationRequest(contractModificationRequestId: string) {
    const request = 'getContractModificationRequest';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request, {
      contractModificationRequestId: contractModificationRequestId,
    });

    return this._apiService.get<app.models.DynamicResponse<app.models.ContractModificationRequest>>(url);
  }

  public getClientDataRequest(requestId: string) {
    const request = 'getClientDataRequestById';
    let queryParams = {
      requestId: requestId,
    };

    const url = this._apiService.buildUrl(
      this._envConfig.PORTAL_BACKEND_APISYNC_URL,
      this.controller,
      request,
      queryParams
    );
    return this._apiService.get<app.models.DynamicResponse<app.models.GetClientDataRequest>>(url);
  }

  public getClientDataRequests(pageIndex: number, pageFilter: app.models.GetClientDataRequestsModelPageFilter) {
    const request = 'getClientDataRequests';
    let data = {
      ...pageFilter,
      page: pageIndex,
      pageSize: DEFAULT_PAGE_SIZE,
    };

    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.post<DynamicPaged<app.models.GetClientDataRequest>>(url, data);
  }

  public saveClientDataRequest(requestBody: app.models.ClientDataRequestSaveModel) {
    const request = 'saveClientDataRequest';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, requestBody, [
      ClientDataRequestEvents.ClientDataRequestSaved,
    ]);
  }

  public getRequestComments(requestId: string, requestTypeCode: string) {
    const request = 'getRequestComments';
    let queryParams = {
      requestId: requestId,
      requestTypeCode: requestTypeCode,
    };

    const url = this._apiService.buildUrl(
      this._envConfig.PORTAL_BACKEND_APISYNC_URL,
      this.controller,
      request,
      queryParams
    );
    return this._apiService.get<app.models.DynamicResponse<app.models.RequestComment[]>>(url);
  }

  public getTechnicalServices() {
    const request = 'getTechnicalServices';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.PartnerModel[]>(url);
  }

  public saveRequestComment(
    requestId: string,
    comment: string,
    requestTypeCode: string,
    dictionaryCommentTypeCode: string | undefined = undefined,
    documents: app.models.RequestXDocument[] | undefined = undefined
  ) {
    const request = 'saveRequestComment';
    let body = {
      requestId: requestId,
      comment: comment,
      requestTypeCode: requestTypeCode,
      dictionaryCommentTypeCode: dictionaryCommentTypeCode,
      documents: documents,
    };

    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.postAsync<app.models.ServerResponseModel>(url, body, [
      RequestCommentEvents.RequestCommentSaved,
    ]);
  }

  public linkUploadedDocumentsToRequest(requestId: string, requestTypeCode: string, documents: any[]) {
    const request = 'linkUploadedDocumentsToRequest';
    let body = {
      requestId: requestId,
      requestTypeCode: requestTypeCode,
      documents: documents,
    };

    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.postAsync<app.models.ServerResponseModel>(url, body, [
      RequestDocumentsEvents.RequestDocumentsLinked,
    ]);
  }

  public getRequestDocuments(requestId: string, requestTypeCode: string) {
    const request = 'getRequestDocuments';
    let queryParams = {
      requestId: requestId,
      requestTypeCode: requestTypeCode,
    };

    const url = this._apiService.buildUrl(
      this._envConfig.PORTAL_BACKEND_APISYNC_URL,
      this.controller,
      request,
      queryParams
    );
    return this._apiService.get<app.models.DynamicResponse<app.models.RequestXDocument[]>>(url);
  }

  public markDocumentsDownloadDate(documentIds: string[], requestTypeCode: string) {
    const request = 'markDocumentsDownloadDate';
    const body = {
      documentIds: documentIds,
      requestTypeCode: requestTypeCode,
    };

    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);
    return this._apiService.postAsync<app.models.ServerResponseModel>(url, body, [
      RequestDocumentsEvents.DocumentsDownloadDateMarked,
    ]);
  }

  public changeAssetDocumentRequestStatus(requestBody: app.models.RequestChangeStatusModel) {
    const request = 'changeAssetDocumentRequestStatus';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, requestBody, [
      RequestEvents.AssetDocumentRequestStatusChanged,
    ]);
  }
  public changeContractModificationRequestStatus(requestBody: app.models.ContractModificationRequestChangeStatusModel) {
    const request = 'changeContractModificationRequestStatus';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, requestBody, [
      ContractModificationRequestEvents.ContractModificationRequestStatusChanged,
    ]);
  }

  public changeClientDataRequestStatus(requestBody: app.models.ClientDataRequestChangeStatusModel) {
    const request = 'changeClientDataRequestStatus';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.postAsync<app.models.ServerResponseModel>(url, requestBody, [
      ClientDataRequestEvents.ClientDataRequestStatusChanged,
    ]);
  }
  public getAdminUsersByModifiedRequestStatus() {
    const request = 'getAdminUsersByModifiedRequestStatus';
    const url = this._apiService.buildUrl(this._envConfig.PORTAL_BACKEND_APISYNC_URL, this.controller, request);

    return this._apiService.get<app.models.DictionaryDataModel[]>(url);
  }
}
